/**
 * SignaturePad Component
 *
 * HTML5 Canvas-based signature capture component.
 * Touch-optimized for mobile use by field technicians.
 *
 * @module components/common/SignaturePad
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, Form, Row, Col } from 'react-bootstrap';

const SignaturePad = ({
  onSave,
  width = 500,
  height = 200,
  lineWidth = 2,
  lineColor = '#000000',
  showSignerFields = true,
  initialSignerName = '',
  initialSignerRole = '',
  initialSignerIdNumber = '',
  disabled = false,
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [signerName, setSignerName] = useState(initialSignerName);
  const [signerRole, setSignerRole] = useState(initialSignerRole);
  const [signerIdNumber, setSignerIdNumber] = useState(initialSignerIdNumber);

  /**
   * Resize canvas to fit container while maintaining aspect ratio
   */
  const resizeCanvas = useCallback(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const containerWidth = container.clientWidth;
    const scaledWidth = Math.min(containerWidth, width);
    const scaledHeight = Math.round((scaledWidth / width) * height);

    // Save current drawing
    const ctx = canvas.getContext('2d');
    const imageData = hasSignature ? ctx.getImageData(0, 0, canvas.width, canvas.height) : null;

    canvas.width = scaledWidth;
    canvas.height = scaledHeight;

    // Restore drawing if there was one
    if (imageData) {
      ctx.putImageData(imageData, 0, 0);
    }

    // Set drawing styles
    ctx.strokeStyle = lineColor;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [width, height, lineColor, lineWidth, hasSignature]);

  useEffect(() => {
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [resizeCanvas]);

  /**
   * Get coordinates from mouse or touch event
   */
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();

    if (e.touches && e.touches.length > 0) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }

    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  /**
   * Start drawing
   */
  const startDrawing = (e) => {
    if (disabled) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);

    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  /**
   * Continue drawing
   */
  const draw = (e) => {
    if (!isDrawing || disabled) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);

    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  /**
   * Stop drawing
   */
  const stopDrawing = (e) => {
    if (!isDrawing) return;
    if (e) e.preventDefault();
    setIsDrawing(false);
  };

  /**
   * Clear the canvas
   */
  const handleClear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  /**
   * Save the signature as data URL and signer info
   */
  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasSignature) return;

    const signatureDataUrl = canvas.toDataURL('image/png');

    onSave({
      signatureDataUrl,
      signerName,
      signerRole,
      signerIdNumber,
    });
  };

  const isValid = hasSignature && (!showSignerFields || signerName.trim());

  return (
    <div className="signature-pad-container" ref={containerRef}>
      {/* Signer information fields */}
      {showSignerFields && (
        <Row className="g-2 mb-3">
          <Col xs={12} md={4}>
            <Form.Group>
              <Form.Label className="small">
                Nombre del Firmante <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                size="sm"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Nombre completo"
                disabled={disabled}
                data-testid="signature-signer-name"
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={4}>
            <Form.Group>
              <Form.Label className="small">Cargo / Rol</Form.Label>
              <Form.Control
                type="text"
                size="sm"
                value={signerRole}
                onChange={(e) => setSignerRole(e.target.value)}
                placeholder="Ej: Gerente de Mantenimiento"
                disabled={disabled}
                data-testid="signature-signer-role"
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={4}>
            <Form.Group>
              <Form.Label className="small">Cedula / ID</Form.Label>
              <Form.Control
                type="text"
                size="sm"
                value={signerIdNumber}
                onChange={(e) => setSignerIdNumber(e.target.value)}
                placeholder="Ej: 1-0123-0456"
                disabled={disabled}
                data-testid="signature-signer-id"
              />
            </Form.Group>
          </Col>
        </Row>
      )}

      {/* Canvas area */}
      <div
        className="signature-canvas-wrapper"
        style={{
          border: '2px dashed #dee2e6',
          borderRadius: '8px',
          padding: '4px',
          backgroundColor: '#fff',
          touchAction: 'none',
          position: 'relative',
        }}
      >
        {!hasSignature && !isDrawing && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: '#adb5bd',
              pointerEvents: 'none',
              fontSize: '0.9rem',
              textAlign: 'center',
            }}
          >
            <i className="bi bi-pen d-block" style={{ fontSize: '1.5rem' }}></i>
            Firme aqui
          </div>
        )}
        <canvas
          ref={canvasRef}
          style={{
            width: '100%',
            cursor: disabled ? 'not-allowed' : 'crosshair',
            display: 'block',
          }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          data-testid="signature-canvas"
        />
      </div>

      {/* Action buttons */}
      <div className="d-flex justify-content-between mt-2">
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleClear}
          disabled={!hasSignature || disabled}
          data-testid="signature-clear-button"
        >
          <i className="bi bi-eraser me-1"></i>
          Limpiar
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={!isValid || disabled}
          data-testid="signature-save-button"
        >
          <i className="bi bi-check-lg me-1"></i>
          Guardar Firma
        </Button>
      </div>
    </div>
  );
};

SignaturePad.propTypes = {
  onSave: PropTypes.func.isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  lineWidth: PropTypes.number,
  lineColor: PropTypes.string,
  showSignerFields: PropTypes.bool,
  initialSignerName: PropTypes.string,
  initialSignerRole: PropTypes.string,
  initialSignerIdNumber: PropTypes.string,
  disabled: PropTypes.bool,
};

export default SignaturePad;
